import {
  Button,
  Card,
  Group,
  SimpleGrid,
  Skeleton,
  Stack,
  Table,
  Text,
  Title,
} from "@mantine/core";
import { BarChart } from "@mantine/charts";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { formatBucketChartLabel } from "@mip-tg-bot-turntable/utils";
import { adminErrText, adminGet, AdminApiError } from "../../api.ts";
import { BotTimezoneNotice } from "../../components/BotTimezoneNotice.tsx";
import { AdminTable } from "../../components/AdminTable.tsx";
import {
  granularityLabel,
  resolveTrendGranularity,
  StatsTrendControls,
  trendRangeLabel,
  type TrendGranularity,
  type TrendRange,
} from "../../components/StatsTrendControls.tsx";

interface BotOverview {
  readonly spinsInventory: number;
  readonly totalUsers: number;
  readonly totalSpinsAllTime: number;
  readonly todaySpins: number;
  readonly todayNewUsers: number;
}

interface PrizeDistributionItem {
  readonly prizeName: string;
  readonly prizeType: string;
  readonly count: number;
}

interface SpinTrendItem {
  readonly bucket: string;
  readonly count: number;
}

interface BotStatsResponse {
  readonly overview: BotOverview;
  readonly prizeDistribution: ReadonlyArray<PrizeDistributionItem>;
  readonly trend: ReadonlyArray<SpinTrendItem>;
  readonly range: TrendRange;
  readonly days: number;
  readonly granularity: TrendGranularity;
  readonly timezone: string;
  readonly statsDay: string;
}

const OVERVIEW_LABELS: Record<keyof BotOverview, string> = {
  spinsInventory: "Spins 库存",
  totalUsers: "累计用户",
  totalSpinsAllTime: "累计抽奖",
  todaySpins: "今日抽奖",
  todayNewUsers: "今日新用户",
};

/** CSV 单元格转义：含逗号 / 引号 / 换行时用双引号包裹并转义内部引号。 */
const csvCell = (v: string | number): string => {
  const s = String(v);
  return /[",\n]/.test(s) ? `"${s.replaceAll('"', '""')}"` : s;
};

export function StatsTab(props: { readonly botId: string }) {
  const { botId } = props;
  const [range, setRange] = useState<TrendRange>("today");
  const [granularity, setGranularity] = useState<TrendGranularity>("day");

  const requestGranularity = resolveTrendGranularity(range, granularity);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["bot", botId, "stats", range, requestGranularity],
    queryFn: () =>
      adminGet<BotStatsResponse>(
        `/bots/${botId}/stats?range=${range}&granularity=${requestGranularity}`,
      ),
    refetchInterval: 60_000,
  });

  const activeRange = data?.range ?? range;
  const activeGranularity = data?.granularity ?? granularity;

  const trendChartData =
    data?.trend.map((it) => ({
      bucketLabel: formatBucketChartLabel(it.bucket, activeGranularity),
      count: it.count,
    })) ?? [];

  const handleExport = () => {
    if (data === undefined) return;
    const lines: string[] = [];
    lines.push("概览");
    lines.push("指标,值");
    for (const key of Object.keys(OVERVIEW_LABELS) as Array<keyof BotOverview>) {
      lines.push(`${csvCell(OVERVIEW_LABELS[key])},${csvCell(data.overview[key])}`);
    }
    lines.push("");
    lines.push("今日奖品命中分布");
    lines.push("奖品名称,类型,命中次数");
    for (const it of data.prizeDistribution) {
      lines.push(`${csvCell(it.prizeName)},${csvCell(it.prizeType)},${csvCell(it.count)}`);
    }
    lines.push("");
    lines.push(`${trendRangeLabel(data.range)}抽奖趋势（按${granularityLabel(data.granularity)}）`);
    lines.push("时间桶,抽奖次数");
    for (const it of data.trend) {
      lines.push(`${csvCell(it.bucket)},${csvCell(it.count)}`);
    }
    const bom = String.fromCharCode(0xfeff);
    const blob = new Blob([bom + lines.join("\n")], {
      type: "text/csv;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `bot-stats-${botId}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const overview = data?.overview;
  const prizeDistribution = data?.prizeDistribution ?? [];

  return (
    <Stack gap="md">
      {data !== undefined ? (
        <BotTimezoneNotice timezone={data.timezone} statsDay={data.statsDay} range={range} />
      ) : null}
      <Group justify="space-between" wrap="wrap">
        <StatsTrendControls
          range={range}
          granularity={granularity}
          onRangeChange={setRange}
          onGranularityChange={setGranularity}
        />
        <Button variant="outline" onClick={handleExport} disabled={data === undefined}>
          导出 CSV
        </Button>
      </Group>

      {isLoading ? (
        <Stack gap="md">
          <SimpleGrid cols={{ base: 2, sm: 3, lg: 5 }} spacing="md">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} h={80} radius="md" />
            ))}
          </SimpleGrid>
          <Skeleton h={200} radius="md" />
          <Skeleton h={200} radius="md" />
        </Stack>
      ) : isError ? (
        <Text c="red" size="sm" ta="center" py="lg">
          {adminErrText((error as AdminApiError).code)}
        </Text>
      ) : (
        <>
          <SimpleGrid cols={{ base: 2, sm: 3, lg: 5 }} spacing="md">
            {overview !== undefined
              ? (Object.keys(OVERVIEW_LABELS) as Array<keyof BotOverview>).map((key) => (
                  <Card key={key} shadow="xs" padding="md" radius="md" withBorder>
                    <Text size="xs" c="dimmed" mb={4}>
                      {OVERVIEW_LABELS[key]}
                    </Text>
                    <Title order={3}>{overview[key].toLocaleString()}</Title>
                  </Card>
                ))
              : null}
          </SimpleGrid>

          <Card shadow="sm" padding="lg" radius="md" withBorder>
            <Text fw={600} size="lg" mb="md">
              今日奖品命中分布
            </Text>
            {prizeDistribution.length === 0 ? (
              <Text c="dimmed" size="sm" ta="center" py="lg">
                今日暂无中奖
              </Text>
            ) : (
              <AdminTable minWidth={400}>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>奖品名称</Table.Th>
                    <Table.Th>类型</Table.Th>
                    <Table.Th>命中次数</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {prizeDistribution.map((item, i) => (
                    <Table.Tr key={i}>
                      <Table.Td>{item.prizeName}</Table.Td>
                      <Table.Td>{item.prizeType}</Table.Td>
                      <Table.Td>{item.count}</Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </AdminTable>
            )}
          </Card>

          <Card shadow="sm" padding="lg" radius="md" withBorder>
            <Text fw={600} size="lg" mb="md">
              {trendRangeLabel(activeRange)}抽奖趋势（按{granularityLabel(activeGranularity)}）
            </Text>
            {trendChartData.length === 0 ? (
              <Text c="dimmed" size="sm" ta="center" py="lg">
                暂无趋势数据
              </Text>
            ) : (
              <BarChart
                h={activeGranularity === "hour" ? 320 : 240}
                data={trendChartData}
                dataKey="bucketLabel"
                series={[{ name: "count", label: "抽奖次数", color: "blue.6" }]}
                tickLine="y"
                withTooltip
              />
            )}
          </Card>
        </>
      )}
    </Stack>
  );
}
